import { readApiError } from '@crow/service-kit/web/api';
import type {
  AcpSessionConfigOption,
  ConversationSettings,
  CrowdexAgentInteraction,
  CrowdexCollaboration,
  CrowdexUIMessage,
} from '@crowdex/agent-contracts';
import type { PersonaReplyRequest } from '@crowdex/agent-contracts/api';
import { canonicalCrowdexAcpAgentId } from '@crowdex/agent-runtime/identity';
import {
  ApiClientError,
  apiClientErrorFromResponse,
  apiResponse,
} from '@crowdex/host-kit/renderer/http';
import { DefaultChatTransport } from 'ai';
import {
  personaMentionIn,
  personaTurnOf,
  readAgentPersonas,
} from '@/lib/conversations/agent-personas';
import { requestPersonaReply } from '@/lib/conversations/persona-reply-client';
import type { LocalWorkspaceServer } from '@/lib/local-runtime';
import type { NativeAgent } from '@/lib/native-desktop';
import { mergeSystemReminders } from './agent-reminders';

/**
 * What a page's request carries besides its message: the conversation's settings (the server
 * saves them with a turn, and equips it as any turn of the conversation), and what only the page
 * knows.
 */
export type CrowdexAgentSessionOptions = {
  settings: ConversationSettings;
  /** Context only the page has (the app the user works with, a handoff's), added to a turn. */
  pageReminder?: (messages: readonly CrowdexUIMessage[]) => string | undefined;
};

type AgentAction = 'chat' | 'generate' | 'steer' | 'cancel' | 'interaction' | 'prepare';
const ACTIVE_TURN_STORAGE_PREFIX = 'crowdex:active-turn:';
const RESUME_RETRY_ATTEMPTS = 3;

async function waitForResumeRetry(response: Response, signal?: AbortSignal | null): Promise<void> {
  const retryAfterHeader = response.headers.get('retry-after');
  const retryAfter = retryAfterHeader === null ? Number.NaN : Number(retryAfterHeader);
  const delay = Number.isFinite(retryAfter)
    ? Math.min(Math.max(retryAfter * 1_000, 50), 1_000)
    : 250;
  await new Promise<void>((resolve, reject) => {
    const finish = () => {
      signal?.removeEventListener('abort', abort);
      resolve();
    };
    const timer = setTimeout(finish, delay);
    const abort = () => {
      clearTimeout(timer);
      signal?.removeEventListener('abort', abort);
      reject(signal?.reason ?? new DOMException('Aborted', 'AbortError'));
    };
    if (signal?.aborted) abort();
    else signal?.addEventListener('abort', abort, { once: true });
  });
}

/** Fetches, retrying a stream resume while its turn is being placed (503 on GET). */
async function fetchRetryingResume(
  input: RequestInfo | URL,
  init?: RequestInit,
): Promise<Response> {
  let response: Response;
  for (let attempt = 0; ; attempt += 1) {
    response = await fetch(input, init);
    if (
      response.status !== 503 ||
      init?.method?.toUpperCase() !== 'GET' ||
      attempt >= RESUME_RETRY_ATTEMPTS - 1
    ) {
      return response;
    }
    await response.body?.cancel().catch(() => undefined);
    await waitForResumeRetry(response, init.signal);
  }
}

/**
 * The chat transport's fetch: a refused turn throws an {@link ApiClientError}, which the chat
 * keeps as its error, so the page knows what is missing (`reason`) and not just a message.
 */
export async function crowdexChatFetch(
  input: RequestInfo | URL,
  init?: RequestInit,
): Promise<Response> {
  const response = await fetchRetryingResume(input, init);
  if (response.ok || isNoActiveStream(response, init)) return response;
  throw await apiClientErrorFromResponse(response);
}

/** A resume of a conversation with no running turn: nothing to follow, not a failure. */
function isNoActiveStream(response: Response, init?: RequestInit): boolean {
  return init?.method?.toUpperCase() === 'GET' && response.status === 204;
}

export function cloudAgentRequestId(agentId: string): string {
  return canonicalCrowdexAcpAgentId(agentId);
}

export type CrowdexAgentRuntimeCapabilities = {
  resume: boolean;
  steer: boolean;
  approvals: boolean;
  structuredOutput: boolean;
  subagents: boolean;
  reasoningStream: boolean;
  sessionConfig: boolean;
  persistentWorkspace: boolean;
  mcp?: boolean;
  clientTools?: boolean;
  fileAttachments?: boolean;
  usage?: boolean;
};

/** A session readied before the conversation's first message. */
export type PreparedAgentSession = {
  ready: boolean;
  /**
   * What the agent reported for it: its models and the one it runs, its effort. `null` when it
   * said nothing (the session did not start, the list could not be read, or the CLI predates it).
   */
  sessionConfigOptions: readonly AcpSessionConfigOption[] | null;
  /** Why `sessionConfigOptions` is `null`, as said to the user. */
  sessionConfigError?: string;
};

/** A CLI from before `/v1/agent/prepare` said the agent's models answers without them. */
export const OUTDATED_CLI_MODELS_ERROR = "Restart Crowdex to load the agent's models.";

/** One authenticated client for every local and cloud agent endpoint. */
export class CrowdexAgentClient {
  private readonly activeTurnIds = new Map<string, string>();
  private readonly capabilitiesByConversation = new Map<string, CrowdexAgentRuntimeCapabilities>();

  constructor(
    private readonly agent: NativeAgent,
    private readonly cloud: boolean,
    private readonly workspaceRoot: string,
    private readonly local: LocalWorkspaceServer | null,
  ) {}

  get available(): boolean {
    return this.cloud || this.local !== null;
  }

  /**
   * Starts the conversation's session before its first message. It answers with what the agent
   * says of the session (its models, the one it runs), for the composer to show right away.
   */
  async prepareSession(
    conversationId: string,
    options: CrowdexAgentSessionOptions,
  ): Promise<PreparedAgentSession> {
    if (this.cloud || !this.available) return { ready: false, sessionConfigOptions: null };
    const result = await this.post('prepare', this.sessionBody(conversationId, options));
    const capabilities = result?.['capabilities'];
    const ready = result?.['ready'] === true;
    if (ready && capabilities !== null && typeof capabilities === 'object') {
      this.capabilitiesByConversation.set(
        conversationId,
        capabilities as CrowdexAgentRuntimeCapabilities,
      );
    }
    const reported = result?.['sessionConfigOptions'];
    if (ready && Array.isArray(reported))
      return { ready, sessionConfigOptions: reported as AcpSessionConfigOption[] };
    const error = result?.['sessionConfigError'];
    return {
      ready,
      sessionConfigOptions: null,
      sessionConfigError: !ready
        ? "The agent's session did not start."
        : typeof error === 'string' && error.trim() !== ''
          ? error
          : OUTDATED_CLI_MODELS_ERROR,
    };
  }

  /** Whether the session being prepared waits for the agent's first install (local only). */
  async installing(signal?: AbortSignal): Promise<boolean> {
    if (this.cloud || !this.available) return false;
    const response = await apiResponse('/api/agent/installing', {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        agent: { id: this.agent.id, command: this.agent.command },
      }),
      ...(signal === undefined ? {} : { signal }),
    }).catch(() => null);
    const result = (await response?.json().catch(() => null)) as { installing?: unknown } | null;
    return result?.installing === true;
  }

  capabilities(conversationId: string): CrowdexAgentRuntimeCapabilities | null {
    return this.capabilitiesByConversation.get(conversationId) ?? null;
  }

  /**
   * The chat transport of one conversation. Each conversation has its own: a turn being prepared
   * (its options can take a while) or followed in one never takes another's turn id.
   */
  createChatTransport(options: {
    conversationId: string;
    sessionOptions: () => CrowdexAgentSessionOptions | Promise<CrowdexAgentSessionOptions>;
    persistence?: () => {
      title: string;
      collaboration: CrowdexCollaboration;
      workspaceRoot: string;
      agentName: string;
      /** Local conversations: the folder's display name and whether it is a Git worktree. */
      workspaceName?: string;
      runtime?: 'local' | 'worktree';
    };
  }): DefaultChatTransport<CrowdexUIMessage> | null {
    if (!this.available) return null;
    const conversationId = options.conversationId;
    let requestStarted = 0;
    let requestTurnId: string | null = null;
    return new DefaultChatTransport<CrowdexUIMessage>({
      api: this.endpoint('chat'),
      fetch: async (input, init) => {
        const started = requestStarted || performance.now();
        const turnId = requestTurnId;
        requestStarted = 0;
        let previous = started;
        const timing = (stage: string) => {
          const now = performance.now();
          console.info('[crowdex-timing]', {
            scope: 'client-turn',
            conversationId,
            turnId,
            stage,
            durationMs: Math.round(now - previous),
            elapsedMs: Math.round(now - started),
          });
          previous = now;
        };
        timing('fetch-start');
        const response = await fetchRetryingResume(input, init);
        timing('response-headers');
        const activeTurnId = response.headers.get('x-crowdex-active-turn-id');
        if (activeTurnId !== null) this.rememberActiveTurn(conversationId, activeTurnId);
        else if (init?.method?.toUpperCase() === 'GET' && response.status === 204) {
          this.forgetActiveTurn(conversationId);
        }
        if (
          response.body !== null &&
          response.ok &&
          response.headers.get('content-type')?.includes('text/event-stream') === true
        ) {
          let firstChunk = true;
          const decoder = new TextDecoder();
          let pending = '';
          const seen = new Set<string>();
          const cleanup = new TransformStream<Uint8Array, Uint8Array>({
            transform(chunk, controller) {
              if (firstChunk) {
                firstChunk = false;
                timing('first-stream-chunk');
              }
              pending += decoder.decode(chunk, { stream: true });
              const lines = pending.split('\n');
              pending = lines.pop() ?? '';
              for (const line of lines) {
                if (!line.startsWith('data:')) continue;
                try {
                  const event = JSON.parse(line.slice(5)) as { type?: string };
                  const type = event.type;
                  if (
                    type !== undefined &&
                    ['text-delta', 'reasoning-delta', 'error'].includes(type) &&
                    !seen.has(type)
                  ) {
                    seen.add(type);
                    timing(`first-${type}`);
                  }
                } catch {
                  /* SSE also includes the non-JSON [DONE] marker. */
                }
              }
              controller.enqueue(chunk);
            },
            flush: () => {
              timing('stream-ended');
              this.forgetActiveTurn(conversationId);
            },
          });
          return new Response(response.body.pipeThrough(cleanup), {
            status: response.status,
            statusText: response.statusText,
            headers: response.headers,
          });
        }
        if (response.ok || isNoActiveStream(response, init)) return response;
        throw await apiClientErrorFromResponse(response);
      },
      // Turns go through the server, which persists them: in the cloud, and locally.
      prepareReconnectToStreamRequest: () => ({
        api: `/api/agent/turns/${encodeURIComponent(conversationId)}/stream`,
        credentials: 'include' as const,
      }),
      prepareSendMessagesRequest: async ({ messages, body }) => {
        const id = conversationId;
        requestStarted = performance.now();
        if (messages.length === 0) throw new Error('A chat request requires a message.');
        const turnId = crypto.randomUUID();
        requestTurnId = turnId;
        this.rememberActiveTurn(id, turnId);
        const persistence = options.persistence?.();
        const sessionOptions = await options.sessionOptions();
        const systemReminder = mergeSystemReminders(
          typeof body?.['systemReminder'] === 'string' ? body['systemReminder'] : undefined,
          sessionOptions.pageReminder?.(messages),
        );
        // `@Designer …` on the conversation's runtime: the persona takes this turn (D51).
        // The saved agents as read, not a cold cache: a persona's turn is never missed.
        const last = messages.at(-1);
        const persona =
          personaMentionIn(last) === null
            ? null
            : personaTurnOf(last, this.agent.id, await readAgentPersonas());
        return {
          body: {
            ...this.sessionBody(id, sessionOptions),
            ...(systemReminder === undefined ? {} : { page: { systemReminder } }),
            ...(persona === null ? {} : { persona: { id: persona.id } }),
            turnId,
            // The runtime uses the canonical transcript only when an ACP session
            // must be rebuilt; a resumed session still consumes only the latest turn.
            messages,
            ...(body?.['rewind'] === true ? { rewind: true } : {}),
            ...(persistence === undefined ? {} : { persistence }),
          },
        };
      },
    });
  }

  /** `request`: the question tool call the text answers (refused once answered elsewhere). */
  async steer(
    conversationId: string,
    text: string,
    messageId: string,
    request?: string,
  ): Promise<boolean> {
    if (!this.available || this.capabilities(conversationId)?.steer === false) return false;
    const turnId = this.activeTurnId(conversationId);
    if (turnId === undefined) return false;
    const response = await this.post(
      'steer',
      { conversationId, turnId, text, messageId, ...(request === undefined ? {} : { request }) },
      undefined,
      true,
      conversationId,
    );
    return response?.['accepted'] === true;
  }

  async cancel(conversationId: string): Promise<void> {
    if (!this.available) return;
    const turnId = this.activeTurnId(conversationId);
    if (turnId === undefined) return;
    await this.post('cancel', { conversationId, turnId }, undefined, true, conversationId);
    this.forgetActiveTurn(conversationId);
  }

  async interact(conversationId: string, interaction: CrowdexAgentInteraction): Promise<boolean> {
    if (
      !this.available ||
      (interaction.type === 'approval' && this.capabilities(conversationId)?.approvals === false)
    ) {
      return false;
    }
    const turnId = this.activeTurnId(conversationId);
    if (turnId === undefined) return false;
    const response = await this.post(
      'interaction',
      { conversationId, turnId, interaction },
      undefined,
      true,
      conversationId,
    );
    return response?.['accepted'] === true;
  }

  async generateText(
    conversationId: string,
    prompt: string,
    options: CrowdexAgentSessionOptions,
    abortSignal?: AbortSignal,
  ): Promise<string> {
    const result = await this.post(
      'generate',
      { ...this.sessionBody(conversationId, options), prompt },
      abortSignal,
    );
    if (typeof result?.['text'] !== 'string') throw new Error('Agent generation failed.');
    return result['text'];
  }

  async generateObject(
    conversationId: string,
    prompt: string,
    outputSchema: Readonly<Record<string, unknown>>,
    options: CrowdexAgentSessionOptions,
    abortSignal?: AbortSignal,
  ): Promise<unknown> {
    const result = await this.post(
      'generate',
      { ...this.sessionBody(conversationId, options), prompt, outputSchema },
      abortSignal,
    );
    if (result === null || !Object.hasOwn(result, 'output')) {
      throw new Error('Structured agent generation failed.');
    }
    return result['output'];
  }

  /**
   * Generates in a throwaway branch of the conversation's agent session, which holds its whole
   * context. Fails when the agent cannot branch its session.
   */
  async generateInConversation(
    conversationId: string,
    prompt: string,
    options: CrowdexAgentSessionOptions,
    abortSignal?: AbortSignal,
  ): Promise<string> {
    const result = await this.post(
      'generate',
      { ...this.sessionBody(conversationId, options), prompt, forkConversation: true },
      abortSignal,
    );
    if (typeof result?.['text'] !== 'string') throw new Error('Agent generation failed.');
    return result['text'];
  }

  /**
   * Everything goes through the server in both profiles: it equips each request from the
   * conversation's settings, and persists turns.
   */
  private endpoint(action: AgentAction, taskId?: string): string {
    if (
      (action === 'steer' || action === 'cancel' || action === 'interaction') &&
      taskId !== undefined
    ) {
      return `/api/agent/turns/${encodeURIComponent(taskId)}/${action}`;
    }
    if (action === 'chat') return '/api/agent/turns';
    if (action !== 'prepare' && action !== 'generate') {
      throw new Error(`Agent ${action} needs an active conversation.`);
    }
    return `/api/agent/${action}`;
  }

  private rememberActiveTurn(conversationId: string, turnId: string): void {
    this.activeTurnIds.set(conversationId, turnId);
    globalThis.sessionStorage?.setItem(`${ACTIVE_TURN_STORAGE_PREFIX}${conversationId}`, turnId);
  }

  private forgetActiveTurn(conversationId: string): void {
    this.activeTurnIds.delete(conversationId);
    globalThis.sessionStorage?.removeItem(`${ACTIVE_TURN_STORAGE_PREFIX}${conversationId}`);
  }

  private activeTurnId(conversationId: string): string | undefined {
    return (
      this.activeTurnIds.get(conversationId) ??
      globalThis.sessionStorage?.getItem(`${ACTIVE_TURN_STORAGE_PREFIX}${conversationId}`) ??
      undefined
    );
  }

  /**
   * `@persona` (design D51): a saved agent of the account answers this message once in the
   * conversation, from where it runs; the user's message and its reply, as stored.
   */
  async personaReply(
    conversationId: string,
    profileId: string,
    message: { id: string; text: string },
    options: CrowdexAgentSessionOptions,
  ): Promise<CrowdexUIMessage[]> {
    const { place, settings } = this.sessionBody(conversationId, options) as Pick<
      PersonaReplyRequest,
      'place' | 'settings'
    >;
    return await requestPersonaReply({ conversationId, profileId, message, place, settings });
  }

  private sessionBody(
    conversationId: string,
    options: CrowdexAgentSessionOptions,
  ): Record<string, unknown> {
    return {
      conversationId,
      place: this.cloud
        ? { agentId: cloudAgentRequestId(this.agent.id) }
        : {
            agent: {
              id: this.agent.id,
              command: this.agent.command,
              args: this.agent.args,
              env: this.agent.env,
            },
            workspaceRoot: this.workspaceRoot,
          },
      settings: options.settings,
    };
  }

  private async post(
    action: Exclude<AgentAction, 'chat'>,
    body: Record<string, unknown>,
    abortSignal?: AbortSignal,
    parseJson = true,
    taskId?: string,
  ): Promise<Record<string, unknown> | null> {
    const attempts = action === 'interaction' ? 3 : 1;
    let lastError: unknown;
    for (let attempt = 0; attempt < attempts; attempt += 1) {
      let response: Response;
      let result: Record<string, unknown> | null;
      const controller = new AbortController();
      // Tool-result acknowledgements run inside the chat's serial update queue.
      // Bound both headers and the response body so a lost reply releases it.
      const timeout =
        action === 'interaction'
          ? setTimeout(
              () => controller.abort(new Error('Agent interaction timed out after 10 seconds.')),
              10_000,
            )
          : undefined;
      const signal =
        abortSignal === undefined
          ? controller.signal
          : AbortSignal.any([abortSignal, controller.signal]);
      try {
        response = await fetch(this.endpoint(action, taskId), {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
          signal,
        });
        result = parseJson
          ? ((await response.json().catch(() => {
              if (signal.aborted) throw signal.reason;
              return null;
            })) as Record<string, unknown> | null)
          : null;
        signal.throwIfAborted();
      } catch (error) {
        lastError = signal.aborted ? signal.reason : error;
        if (abortSignal?.aborted || attempt === attempts - 1) throw lastError;
        await waitForResumeRetry(new Response(null, { status: 503 }), abortSignal);
        continue;
      } finally {
        clearTimeout(timeout);
      }
      if (response.ok) return result;
      const error = new Error(
        readApiError(result).message ?? `Agent ${action} failed with HTTP ${response.status}.`,
      );
      if (response.status < 500 || attempt === attempts - 1) throw error;
      lastError = error;
      await waitForResumeRetry(new Response(null, { status: 503 }), abortSignal);
    }
    throw lastError;
  }
}

/**
 * The transport while no agent can run: a send fails with what is missing, and a conversation the
 * server persists can still be followed.
 */
export function createUnavailableCrowdexChatTransport(
  serverPersisted = false,
): DefaultChatTransport<CrowdexUIMessage> {
  return new DefaultChatTransport<CrowdexUIMessage>({
    api: '/api/agent/turns',
    fetch: (input, init) => {
      if (init?.method?.toUpperCase() === 'GET') return crowdexChatFetch(input, init);
      return Promise.reject(
        new ApiClientError(
          'No agent is available to answer. Choose or install one.',
          424,
          'unconfigured',
          [],
          'no_agent',
        ),
      );
    },
    ...(serverPersisted
      ? {
          prepareReconnectToStreamRequest: ({ id }) => ({
            api: `/api/agent/turns/${encodeURIComponent(id)}/stream`,
            credentials: 'include' as const,
          }),
        }
      : {}),
  });
}
